import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  BanknotesIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  EllipsisHorizontalCircleIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { LogoMark, Modal } from '@lipaflow/ui'
import { useQueryClient } from '@tanstack/react-query'
import portalApi, { clearPortalToken } from '../services/api'
import { firebaseLogout } from '@lipaflow/shared'
import type { VerticalNavItem } from '@lipaflow/shared'
import { usePortalVertical } from '../context/PortalVerticalContext'

/**
 * A portal tab. Same shape as a vertical module's nav item (ADR-0004), so a
 * shell can concatenate the shared money tabs with whatever its Tier 2
 * package declares and pass the lot straight through.
 */
export type PortalNavItem = VerticalNavItem

/** The money tabs every vertical shares. */
/** Payments, receipts and plans: only where the organisation collects through
 *  LipaFlow (not a school on the CBC suite alone). */
export const COLLECTIONS = 'collections'

export const MONEY_NAV_ITEMS: PortalNavItem[] = [
  { to: '/', label: 'Balance', icon: BanknotesIcon, end: true, requiresFeature: COLLECTIONS },
  { to: '/receipts', label: 'Receipts', icon: DocumentTextIcon, requiresFeature: COLLECTIONS },
  { to: '/payment-plans', label: 'Payment plans', icon: CalendarDaysIcon, requiresFeature: COLLECTIONS },
]

interface PortalLayoutProps {
  /** Full nav list for this shell app. Defaults to the shared money tabs. */
  navItems?: PortalNavItem[]
  /** Overrides the labels-derived title (e.g. force "Tenant Portal"). */
  title?: string
  headerActions?: ReactNode
}

/**
 * Renter/parent portal shell — mobile-first.
 *
 * Payers arrive on a phone, usually from a WhatsApp link, often on a poor
 * connection. So: one column, generous tap targets (min 48px), tabs that
 * stay reachable at the bottom on small screens, and no decorative depth.
 */
export default function PortalLayout({ navItems = MONEY_NAV_ITEMS, title, headerActions }: PortalLayoutProps) {
  const { labels, isFeatureEnabled, tenant_name } = usePortalVertical()
  const visibleNavItems = navItems.filter(
    (item) => !item.requiresFeature || isFeatureEnabled(item.requiresFeature),
  )
  const portalTitle = title ?? `${labels.payer} Portal`

  // A phone fits four readable tabs. Everything else goes behind "More"
  // rather than shrinking eleven tabs to 33px each.
  const MAX_BOTTOM_TABS = 4
  const needsMore = visibleNavItems.length > MAX_BOTTOM_TABS + 1
  const bottomTabs = needsMore ? visibleNavItems.slice(0, MAX_BOTTOM_TABS) : visibleNavItems
  const overflowTabs = needsMore ? visibleNavItems.slice(MAX_BOTTOM_TABS) : []
  const [moreOpen, setMoreOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [leaving, setLeaving] = useState(false)
  async function leave() {
    setLeaving(true)
    let signOutWarning: string | undefined
    try {
      await portalApi.post('/logout', undefined, { timeout: 5000 })
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status
      if (status !== 401 && status !== 403) {
        signOutWarning = 'Signed out on this device. We could not reach the server to revoke the previous session.'
      }
    }
    await queryClient.cancelQueries()
    clearPortalToken()
    localStorage.removeItem('lipaflow.paylink.phone')
    queryClient.clear()
    try { await firebaseLogout() } catch { signOutWarning = 'Portal access is cleared. Phone sign-out could not finish; close this browser if it is shared.' }
    navigate('/view', { replace: true, state: { signOutWarning } })
  }
  const location = useLocation()
  useEffect(() => setMoreOpen(false), [location.pathname])
  const overflowActive = overflowTabs.some((item) => location.pathname.startsWith(item.to))

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <header className="sticky top-0 z-30 border-b border-hairline bg-surface/90 backdrop-blur">
        <div className="max-w-content lg:max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <LogoMark size="md" markStyle="standalone" />
          <div className="leading-tight min-w-0">
            <span className="block font-outfit font-semibold text-ink truncate">
              {portalTitle}
            </span>
            <span className="block font-inter text-caption text-ink-faint truncate">
              {tenant_name ? `${tenant_name} · Powered by LipaFlow` : 'Powered by LipaFlow'}
            </span>
          </div>
        </div>

        <div className="max-w-content lg:max-w-5xl mx-auto px-4 pb-2 flex flex-wrap gap-3 text-sm">
          <p className="flex-1 self-center text-caption text-ink-muted">To use another organisation, sign out and sign in again.</p>
          <button type="button" disabled={leaving} onClick={() => void leave()} className="min-h-11 rounded-button border border-hairline px-4 text-sm font-medium text-ink-muted hover:bg-surface-alt">{leaving ? 'Signing out…' : 'Sign out'}</button>
        </div>

        {/* Tabs inline on tablet/desktop; the bottom bar takes over on phones. */}
        <nav aria-label="Main" className="hidden sm:block border-t border-hairline">
          <div className="max-w-content lg:max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2 px-4 py-3 text-body-sm font-medium border-b-2 whitespace-nowrap',
                    'transition-colors duration-fast ease-standard',
                    isActive
                      ? 'border-accent text-accent'
                      : 'border-transparent text-ink-muted hover:text-ink hover:border-hairline'
                  )
                }
              >
                {item.icon ? <item.icon className="w-4 h-4" /> : null}
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
        {headerActions && <div className="max-w-content mx-auto px-4 pb-2">{headerActions}</div>}
      </header>

      {/* pb leaves room for the fixed bottom bar on phones. */}
      <main id="main-content" tabIndex={-1} className="max-w-content lg:max-w-5xl mx-auto px-4 py-6 pb-28 sm:pb-10 space-y-6">
        <Outlet />
      </main>

      <nav className="safe-bottom sm:hidden fixed bottom-0 inset-x-0 z-30 border-t border-hairline bg-surface" aria-label="Main">
        <div className="flex items-stretch">
          {bottomTabs.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'flex-1 flex flex-col items-center justify-center gap-1 h-16 text-caption font-medium',
                  'transition-colors duration-fast ease-standard',
                  isActive ? 'text-accent bg-accent-soft' : 'text-ink-faint'
                )
              }
            >
              {item.icon ? <item.icon className="w-5 h-5" /> : null}
              {item.label}
            </NavLink>
          ))}
          {needsMore && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-expanded={moreOpen}
              className={clsx(
                'flex-1 flex flex-col items-center justify-center gap-1 h-16 text-caption font-medium',
                overflowActive ? 'text-accent' : 'text-ink-faint'
              )}
            >
              <EllipsisHorizontalCircleIcon className="w-5 h-5" />
              More
            </button>
          )}
        </div>
      </nav>

      {needsMore && (
        <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="More pages">
            <div className="grid grid-cols-3 gap-2 p-4">
              {overflowTabs.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    clsx(
                      'flex flex-col items-center justify-center gap-2 min-h-[72px] rounded-card border text-caption font-medium',
                      isActive ? 'border-accent text-accent bg-accent-soft' : 'border-hairline text-ink-muted'
                    )
                  }
                >
                  {item.icon ? <item.icon className="w-6 h-6" /> : null}
                  {item.label}
                </NavLink>
              ))}
            </div>
        </Modal>
      )}
    </div>
  )
}
